import { useRef, useState, type RefObject } from 'react';

/** A drag handle on a side panel's inner edge (`edge`: the panel side it sits on). Dragging
 * reports the panel's new width in px, clamped to [min, max()]; double-click resets it. */
export function PanelResizer({ panel, edge, min, max, onResize, onReset }: { panel: RefObject<HTMLElement>; edge: 'left' | 'right'; min: number; max: () => number; onResize: (width: number) => void; onReset: () => void }) {
  const drag = useRef<{ x: number; width: number } | null>(null);
  const [active, setActive] = useState(false);
  const end = () => {
    drag.current = null;
    setActive(false);
  };
  return (
    <div
      className={`panel-resizer ${active ? 'dragging' : ''}`}
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize, double-click to reset"
      onPointerDown={(e) => {
        if (e.button !== 0 || !panel.current) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { x: e.clientX, width: panel.current.getBoundingClientRect().width };
        setActive(true);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.x;
        const width = d.width + (edge === 'right' ? dx : -dx);
        onResize(Math.round(Math.min(Math.max(width, min), Math.max(min, max()))));
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={onReset}
    />
  );
}
